import { useCallback } from 'react'

import styled from '@emotion/styled'

import { DocumentFillIcon } from '@arcade/web/icons/react'
import { Header, spacing } from '@cash-design-system/react'
import { useLogCashWebCdfEvent, useLogPageClickEventstream } from '@cash-web/shared-feature-analytics'
import { CardItemExternalAnchor, CardList } from '@cash-web/support/ui-components'
import type {
  CustomerSupportAccessOpenNodeSelectedType,
  CustomerSupportAccessOpenNodeTrigger,
} from '@squareup/customer-data-events-web'
import { CustomerSupportAccessOpenNode } from '@squareup/customer-data-events-web'

import type { ArticleListing } from '../../../types'
import { useAnalyticsContext } from '../../contexts/Analytics'

const StyledHeader = styled(Header)`
  margin-bottom: ${spacing.m};
`
interface ArticleListProps {
  tabletColumns?: 1 | 2
  trigger: CustomerSupportAccessOpenNodeTrigger
  data: ArticleListing[]
  title?: string
  analyticsType?: CustomerSupportAccessOpenNodeSelectedType
}

const ArticleList = ({ data, tabletColumns = 1, title, trigger, analyticsType = 'SUGGESTED' }: ArticleListProps) => {
  const logCdfEvent = useLogCashWebCdfEvent()
  const logPageClickEvent = useLogPageClickEventstream()
  const { viewToken } = useAnalyticsContext()

  const trackEvent = useCallback(
    (article: ArticleListing & { index: number }) => {
      logPageClickEvent({
        action: 'LINK_CLICK',
        parent_identifier: 'help-article-list',
        identifier: 'help-article-list-item chevron-after',
        type: 'A',
        title: '',
        text: article.title,
        target: `/help/${article.token}`,
      })
      logCdfEvent(
        CustomerSupportAccessOpenNode({
          trigger: trigger,
          viewToken: viewToken,
          selectedType: analyticsType,
          selectedTypeCount: data.length,
          selectedTypeIndex: article.index,
          nodeToken: article.id,
        })
      )
    },
    [data, logCdfEvent, logPageClickEvent, analyticsType, viewToken, trigger]
  )

  return (
    <>
      {title ? (
        <StyledHeader variant="header-3" color="secondaryLabel" as="h2">
          {title}
        </StyledHeader>
      ) : undefined}
      <CardList
        divider
        columns={{ mobile: 1, tablet: tabletColumns }}
        data={data}
        render={article => (
          <CardItemExternalAnchor
            to={`/help/${article.token}-${article.slug}`}
            title={article.title}
            onClickTrackEvent={() => trackEvent(article)}
            icon={article.icon ? <DocumentFillIcon /> : undefined}
          />
        )}
      />
    </>
  )
}

export default ArticleList
